@use 'sass:color';
@use '@/sass/variables';
@use '@/sass/utils/mixins';

$cell-padding-vertical: 2px;
$standard-border: 1px solid variables.$moon-gray;
$group-border: 2px solid variables.$gray;

.gridContainer {
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
}

.headerRow {
  display: flex;
  flex-shrink: 0;
}

.stickyHeader {
  position: relative;
  flex-shrink: 0;
  background: variables.$white;
  border-right: $group-border;
  z-index: 3;
  overflow: hidden;
}

.scrollableHeader {
  flex: 1;
  overflow: hidden;
  position: relative;
  background: variables.$white;
  z-index: 2;
}

.bodyRow {
  display: flex;
  flex: 1;
  overflow: hidden;
}

.stickyRows {
  position: relative;
  flex-shrink: 0;
  overflow: hidden;
  background: variables.$white;
  border-right: $group-border;
  z-index: 1;
}

.mainGrid {
  flex: 1;
  overflow: auto;
  position: relative;
  background: variables.$white;
}

.virtualContainer {
  position: relative;
}

.groupHeader {
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-right: $standard-border;
  border-bottom: $standard-border;
  background: variables.$near-white;
  font-weight: 600;
  font-size: variables.$font-size-6;
  padding: variables.$spacing-small;
  box-sizing: border-box;
  overflow: hidden;

  &.groupBorder {
    border-right: $group-border;
  }
}

.groupHeaderText {
  display: flex;
  flex-direction: row;
  align-items: center;
  min-width: 0;
  flex: 0 1 auto;
  margin-right: variables.$spacing-small;
  will-change: transform;
}

.groupHeaderTextContent {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
  margin-left: variables.$spacing-small;
}

.groupHeaderLabel {
  color: variables.$dark-gray;
  font-size: variables.$font-size-8;
  line-height: 1.25;
  font-weight: 400;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.groupHeaderTitle {
  font-weight: 500;
  color: variables.$near-black;
  line-height: 1.25;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  .label {
    font-size: variables.$font-size-8;
    font-weight: 400;
    color: variables.$dark-gray;
  }
}

.groupHeaderActions {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.columnHeader {
  position: absolute;
  border-bottom: $standard-border;
  background: variables.$near-white;
  box-sizing: border-box;
  font-size: variables.$font-size-7;
  font-weight: 500;
  color: variables.$near-black;
  line-height: variables.$line-height-solid;
  display: flex;
  align-items: center;
  padding: variables.$spacing-small;
  width: 100%;
  height: 100%;

  &.selected {
    background-color: color.adjust(variables.$gray, $lightness: 12%, $space: hsl);
  }
}

.cell {
  position: absolute;
  border-right: $standard-border;
  border-bottom: $standard-border;
  background: variables.$white;
  box-sizing: border-box;
}

.zoneBorderRight {
  position: absolute;
  top: 0;
  right: 0;
  width: 2px;
  height: 100%;
  background: variables.$gray;
  z-index: 10;
  pointer-events: none;
}

.zoneBorderBottom {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 2px;
  background: variables.$gray;
  z-index: 10;
  pointer-events: none;
}

.rowHeader {
  width: 100%;
  height: 100%;
  font-size: variables.$font-size-7;
  font-weight: 500;
  color: variables.$near-black;
  line-height: variables.$line-height-solid;
  overflow: hidden;
  padding: $cell-padding-vertical variables.$spacing-small;

  &:nth-child(n + 2) {
    border-left: $standard-border;
  }

  &:first-child {
    display: flex;
    align-items: center;
  }

  &.selected {
    background-color: color.adjust(variables.$gray, $lightness: 12%, $space: hsl);
  }
}

.rowHeaderContainer {
  border-bottom: $standard-border;
  border-right: $standard-border;
  background-color: variables.$near-white;
}

.rowHeaderStickyContainer {
  position: sticky;
  z-index: 3;
  left: 0;
  height: 100%;
}

.rowColumnHeaderStickyContainer {
  position: sticky;
  top: 0;
  left: 0;
  z-index: 4;
  display: flex;
  flex-direction: column;
}

.rowColumnHeaderGroupSpacer {
  background-color: variables.$white;
  flex-shrink: 0;
}

.rowColumnHeaderRow {
  display: flex;
  flex-shrink: 0;
}

.rowColumnHeaderCell {
  position: relative;
  flex-shrink: 0;
}

.container {
  width: 100%;
  height: 100%;
  overflow: hidden;
  user-select: none;
  position: relative;
}

.columnHeaderContainer {
  border-bottom: $standard-border;
  border-right: $standard-border;
  background-color: #f5f6f7;

  &.groupBorder {
    border-right: $group-border;
  }
}

.CellAlert {
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 0 8px 8px 0;
  border-color: transparent variables.$dark-red transparent transparent;
  position: absolute;
  right: 2px;
  top: 2px;
}

.CellContainer {
  border-bottom: $standard-border;
  border-right: $standard-border;

  &.groupBorder {
    border-right: $group-border;
  }
}

.Cell {
  width: 100%;
  height: 100%;
  font-size: variables.$font-size-7;
  color: variables.$near-black;
  line-height: variables.$line-height-copy;
  border: 2px solid transparent;
  padding: $cell-padding-vertical mixins.rem(14px);
  outline: 0;
  background-color: variables.$white;
  user-select: none;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  display: flex;
  align-items: center;

  &.Highlighted {
    background-color: color.adjust(variables.$blue, $lightness: 60%, $space: hsl);
  }

  &.OriginSelected {
    border-color: variables.$blue;

    input[type='datetime-local'] {
      height: 0;
      padding: 0;
      margin: 0;
    }
  }

  &.Active,
  &[contenteditable='true']:focus {
    box-shadow: 0 2px 6px 2px rgb(60 64 67 / 15%);
    white-space: unset;
    text-overflow: unset;
  }

  &[data-celltype='number'],
  &[data-celltype='doseWeight'],
  &[data-celltype='doseVolume'] {
    text-align: right;
  }

  &.Invalid {
    background-color: #f3b2b8;
    color: variables.$dark-red;

    &.Highlighted {
      background-color: #dea4a9;
    }

    &.OriginSelected {
      border: 2px solid variables.$dark-red;
    }
  }

  &.Warning {
    background-color: color.adjust(variables.$gold, $lightness: 45%, $space: hsl);
    border-color: variables.$gold;

    &.OriginSelected {
      border-color: variables.$blue;
    }
  }

  &.NotSaved {
    background-color: color.adjust(variables.$gold, $lightness: 40%, $space: hsl);
    border-color: variables.$gold;
  }

  &.ReadOnly {
    background-color: variables.$near-white;
  }

  &.Loading {
    display: inline-block;
    height: 100%;
    width: 100%;
    position: relative;
    overflow: hidden;
    background-color: #dddbdd;

    &::after {
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      z-index: 1;
      transform: translateX(-100%);
      background-image: linear-gradient(
        90deg,
        rgba(#fff, 0) 0,
        rgba(#fff, 0.2) 20%,
        rgba(#fff, 0.5) 60%,
        rgba(#fff, 0)
      );
      animation: loadingAnimation 1s infinite;
      content: '';
    }
  }

  .TimestampInput {
    visibility: hidden;
    height: 0;
    width: 100%;
    position: absolute;
    left: 0;
    bottom: 0;
    z-index: 0;
    pointer-events: none;
    margin: 0;
    padding: 0;
    min-height: 0;
    opacity: 0;
  }

  .TimestampValue {
    word-wrap: break-word;
  }

  .DoseUnit {
    margin-left: 2px;
    color: variables.$dark-gray;
    user-select: none;
  }

  // The blank mask sizes its slots in `ch`, so rendered digits need a uniform advance to register
  // against it.
  .TabularNumerals {
    font-variant-numeric: tabular-nums;
  }

  .BlankDateTimeMask {
    color: variables.$dark-gray;
    user-select: none;
    font-variant-numeric: tabular-nums;
  }

  .BlankDateTimeMaskSlot {
    display: inline-block;
    width: 1ch;
    text-align: center;
  }

  .Chip {
    display: inline-flex;
    align-items: center;
    padding: 2px variables.$spacing-small;
    border-radius: 12px;
    background-color: variables.$near-white;
    border: 1px solid variables.$moon-gray;
    font-size: variables.$font-size-8;
    font-weight: 500;
    line-height: variables.$line-height-solid;
    color: variables.$near-black;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    &.planned {
      background-color: variables.$washed-blue;
      border-color: variables.$blue;
      color: variables.$blue;
    }

    &.collected {
      background-color: variables.$washed-green;
      border-color: variables.$green;
      color: variables.$green;
    }

    &.cancelled {
      background-color: variables.$washed-red;
      border-color: variables.$dark-red;
      color: variables.$dark-red;
    }

    &.administered {
      background-color: variables.$washed-green;
      border-color: variables.$green;
      color: variables.$green;
    }
  }
}

.livingStatusContainer {
  display: flex;
  align-items: center;
  height: 23px;
}

.livingStatusChip {
  display: inline-flex;
  align-items: center;
  padding: 2px variables.$spacing-small;
  border-radius: 12px;
  font-size: variables.$font-size-8;
  font-weight: 500;
  line-height: variables.$line-height-solid;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &.alive {
    background-color: variables.$washed-green;
    border: 1px solid variables.$green;
    color: variables.$green;
  }

  &.deceased {
    background-color: variables.$silver;
    border: 1px solid variables.$dark-gray;
    color: variables.$dark-gray;
  }
}

@keyframes loadingAnimation {
  100% {
    transform: translateX(100%);
  }
}

.columnType {
  width: 100%;
  display: flex;
  align-items: center;
}

.columnTextContainer {
  margin-left: variables.$spacing-small;
}

.columnName,
.columnUnit {
  line-height: 1.25;
  max-width: 85px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.columnUnit {
  font-weight: 400;
  color: variables.$dark-gray;
  font-size: variables.$font-size-8;
}

.columnDate {
  color: variables.$dark-gray;
  font-size: variables.$font-size-8;
  width: 100%;
}

.columnTypeSymbolContainer {
  position: relative;
}

.columnTypeSymbol {
  width: 20px;
  height: 20px;
  border-radius: variables.$border-radius-circle;
  border: $standard-border;
  background-color: variables.$white;
  font-size: variables.$font-size-7;
  color: variables.$dark-gray;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: variables.$line-height-solid;
}

.columnTypeLockSymbol {
  position: absolute;
  top: 12px;
  left: 12px;
}

.columnTypeRequiredSymbol {
  position: absolute;
  top: -4px;
  left: -4px;
}

.columnHeaderDropdown {
  border-radius: 2px;
  line-height: 0;
  cursor: pointer;

  &:hover {
    background-color: variables.$gray;
  }
}

.ReadonlyColumn {
  background-color: variables.$near-white;
}

:global(.dt-cell-flash-green) {
  animation: deviceReadingFlashGreen 300ms ease-in-out 3;
}

:global(.dt-cell-flash-blue) {
  animation: deviceReadingFlashBlue 300ms ease-in-out 3;
}

@keyframes deviceReadingFlashGreen {
  0%,
  100% {
    border-color: transparent;
  }

  50% {
    border-color: variables.$green;
  }
}

@keyframes deviceReadingFlashBlue {
  0%,
  100% {
    border-color: transparent;
  }

  50% {
    border-color: variables.$blue;
  }
}
